import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUsers,
  userDisplayName,
  userInitials,
  type OrganizationUser,
} from "../../services/users";
import { getAssignableRoles, type AssignableRole } from "../../services/userPermissions";
import { useAuth } from "../../hooks/useAuth";
import { Button } from "../../components/Button";
import { AddUserModal, type AddUserSubmission } from "../admin/AddUserModal";
import { PendingInvitations } from "./PendingInvitations";
import { GetStartedVisibilityCard } from "../onboarding/GetStartedVisibilityCard";
import { useOrganizationInvitations } from "./useOrganizationInvitations";
import { roleLabel, formatUserDate } from "./userDisplay";
import "../../components/DataTable.css";
import "./UsersSection.css";

/**
 * The organization's people, as a table.
 *
 * A table because that is what an admin users screen looks like everywhere else, and it puts the
 * answer to "who is an admin?" in a column rather than behind a click.
 *
 * **Everyone is listed, the signed-in admin included.** An earlier version filtered them out on the
 * reasoning that you cannot change your own role — which made an organization with a single admin
 * appear to have none. Your own row is marked instead, and the actions you cannot take are omitted
 * from the page it opens.
 *
 * A row opens that person's own page rather than expanding a panel here: role, page access and
 * activity is more than a row can hold, and a route can be linked to.
 *
 * **Adding someone is an invitation, and it happens here.** It used to require platform staff, who
 * created the account and passed a temporary password along by hand. Now an admin sends a link, the
 * person sets their own password, and the pending table below says who has one outstanding.
 * Removing someone lives on that person's own page, beside their role.
 */
export const UsersSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const signedInId = user?.profile?.id;

  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [roles, setRoles] = useState<AssignableRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const invitations = useOrganizationInvitations();

  useEffect(() => {
    let cancelled = false;

    getUsers()
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load your organization's users.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Its own request, and its own failure. The invite dialog falls back to the two roles it knows
    // if this never arrives, which is a better answer than no invite control at all.
    getAssignableRoles()
      .then((list) => {
        if (!cancelled) setRoles(list);
      })
      .catch(() => {
        /* The dialog's own defaults cover this. */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const adminCount = users.filter((u) => u.role !== "user").length;

  /** The API records the issuing admin by id; only this screen has the names. */
  const invitedByName = (userId: string | null): string => {
    if (!userId) return "Unknown";
    const issuer = users.find((u) => u.id === userId);
    return issuer ? userDisplayName(issuer) : "Unknown";
  };

  const handleInvite = async (submission: AddUserSubmission) => {
    const done = await invitations.invite(submission);
    if (done) setInviteOpen(false);
  };

  // Split by tone rather than by recency: the two go to different regions, and a refusal must not
  // be displaced by a success that happened to settle after it on a different row.
  const confirmations = invitations.notices.filter((n) => n.tone === "success");
  const refusals = invitations.notices.filter((n) => n.tone === "error");

  return (
    <div className="org-users-page">
      {/*
        Two elements, because a confirmation and a refusal are not the same announcement.

        The polite one is mounted for the life of the screen, with or without anything to say: a
        live region inserted at the same moment as its text is frequently not announced at all,
        which would make every confirmation here silent. It is never display:none either — a hidden
        region is out of the accessibility tree, which is the same silence by another route — so it
        collapses its own flex gap instead.

        The refusal is a separate `role="alert"`, not the same node with its `role` swapped. That
        keeps the interruption a refusal deserves, and it means the difference between "sent" and
        "refused" is carried by something other than the colour of the box.

        Both hold a line per invitation rather than a single message. Two rows can be working at
        once, so one slot meant the second to settle silently ate the first's result — a resend
        that failed and a withdrawal that succeeded a moment later left the admin never told the
        email had not gone out. `aria-atomic` is therefore false on both: `role="alert"` is
        implicitly atomic in the same way `role="status"` is, and with more than one line an atomic
        region re-reads every earlier sentence each time a new one arrives.

        The refusal region stays conditionally mounted while the polite one is permanent, which is
        the one place they differ on purpose: inserting a `role="alert"` is announced reliably,
        where inserting a `role="status"` often is not.
      */}
      <div
        className="org-users__notice-region"
        role="status"
        aria-live="polite"
        aria-atomic="false"
      >
        {confirmations.map((notice) => (
          <div
            key={notice.key}
            className="org-users__notice org-users__notice--success"
          >
            {notice.text}
          </div>
        ))}
      </div>

      {refusals.length > 0 && (
        <div
          className="org-users__notice org-users__notice--error"
          role="alert"
          aria-atomic="false"
        >
          {refusals.map((notice) => (
            <p key={notice.key} className="org-users__notice-line">
              {notice.text}
            </p>
          ))}
        </div>
      )}

      <section className="org-users data-table-card">
        <header className="data-table-card__header">
          <div>
            <h2 className="data-table-card__title">Users</h2>
            <p className="data-table-card__subtitle">
              {loading
                ? "Loading…"
                : `${users.length} ${users.length === 1 ? "person" : "people"}, ${adminCount} ${
                    adminCount === 1 ? "administrator" : "administrators"
                  }. Select someone to manage their role and access.`}
            </p>
          </div>
          {/*
            Hidden when this API has no invitations endpoints, which is what a web build deployed
            ahead of the API looks like. Offering a button that can only fail is worse than not
            offering it for the few minutes the two are out of step.
          */}
          {invitations.supported && (
            <Button
              variant="primary"
              data-testid="invite-user"
              onClick={() => setInviteOpen(true)}
            >
              Invite user
            </Button>
          )}
        </header>

        {error && (
          <div className="org-users__error" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <p className="org-users__muted">Loading users…</p>
        ) : (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Role</th>
                  <th scope="col">Page access</th>
                  <th scope="col">Last active</th>
                  <th scope="col">Added</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  /*
                    The row is the link target, but the accessible control is the name button rather
                    than a click handler on the <tr>: a row is not focusable and gives a keyboard user
                    nothing to activate.
                  */
                  <tr
                    key={u.id}
                    className="data-table__row--clickable"
                    onClick={() => navigate(`/settings/users/${u.id}`)}
                  >
                    <td>
                      <div className="data-table__identity">
                        <span className="data-table__avatar" aria-hidden="true">
                          {userInitials(u)}
                        </span>
                        <button
                          type="button"
                          className="data-table__link-button"
                          onClick={(e) => {
                            // The row handler would fire too and navigate twice.
                            e.stopPropagation();
                            navigate(`/settings/users/${u.id}`);
                          }}
                        >
                          {userDisplayName(u)}
                        </button>
                        {u.id === signedInId && (
                          <span className="data-table__tag">You</span>
                        )}
                      </div>
                    </td>
                    <td className="data-table__muted">{u.email}</td>
                    <td>
                      <span
                        className={`data-table__badge${
                          u.role === "user" ? "" : " data-table__badge--primary"
                        }`}
                      >
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td className="data-table__muted">
                      {u.role === "user" ? "Set per page" : "All pages"}
                    </td>
                    <td className="data-table__muted">
                      {formatUserDate(u.lastLoginAt)}
                    </td>
                    <td className="data-table__muted">
                      {formatUserDate(u.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/*
        Held back until both requests have landed. "Invited by" is resolved against the users list,
        which is a separate request, so painting this first shows "Unknown" beside every row and
        then swaps in the names a moment later.
      */}
      {!loading && !invitations.loading && (
        <PendingInvitations
          invitations={invitations.invitations}
          invitedByName={invitedByName}
          busyIds={invitations.busyIds}
          onResend={invitations.resend}
          onRevoke={invitations.revoke}
        />
      )}

      {/*
        The only way back to a dismissed Get Started checklist. It has no nav entry by design, so
        without this, hiding it on day one is permanent. Renders nothing against an API that has no
        checklist endpoint.
      */}
      <GetStartedVisibilityCard />

      {/*
        The same dialog platform staff use, with the copy and the role list changed. An invitation
        creates no password to pass along, so the description says what actually happens next.

        No name fields here. The invitations API takes an address and a role and nothing else, so a
        name typed on this path was serialized, ignored by model binding and discarded — an admin
        filled it in and it disappeared without a trace. The invitee gives their own names when
        they accept the invitation, so nothing is lost by not asking twice.
      */}
      <AddUserModal
        isOpen={inviteOpen}
        collectNames={false}
        title="Invite a user"
        description="They get an email with a link to set their own password. The link works for 7 days."
        submitLabel="Send invitation"
        busyLabel="Sending…"
        roles={roles.length > 0 ? roles : undefined}
        onClose={() => {
          setInviteOpen(false);
          invitations.clearInviteError();
        }}
        onSubmit={handleInvite}
        busy={invitations.inviting}
        error={invitations.inviteError}
      />
    </div>
  );
};
